package com.swetdot.swetgram.ui.picso

import androidx.compose.animation.core.Animatable
import androidx.compose.foundation.background
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.gestures.rememberTransformableState
import androidx.compose.foundation.gestures.transformable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.hapticfeedback.HapticFeedbackType
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.LocalHapticFeedback
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.semantics.clearAndSetSemantics
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import com.swetdot.swetgram.domain.PicsoPostItem
import com.swetdot.swetgram.domain.isActiveAt
import com.swetdot.swetgram.ui.design.*
import com.swetdot.swetgram.ui.theme.SwetgramThemeTokens
import org.jetbrains.compose.resources.stringResource
import swetgram.shared.generated.resources.*
import kotlin.time.Instant
import kotlinx.coroutines.delay

@Composable
fun PicsoDetailViewer(
    post: PicsoPostItem,
    now: () -> Instant,
    loadImage: suspend (String) -> ByteArray,
    busy: Boolean,
    onDismiss: () -> Unit,
    onReact: (String) -> Unit,
    onToggleSave: () -> Unit,
    onDelete: () -> Unit,
    onOpenProfile: () -> Unit,
    onShare: () -> Unit,
    onReport: () -> Unit,
) {
    var confirmDelete by remember(post.id) { mutableStateOf(false) }
    var scale by remember(post.id) { mutableFloatStateOf(1f) }
    var offset by remember(post.id) { mutableStateOf(Offset.Zero) }
    var currentTime by remember(post.id) { mutableStateOf(now()) }
    var burstEmoji by remember(post.id) { mutableStateOf<String?>(null) }
    var burstSequence by remember(post.id) { mutableIntStateOf(0) }
    var previousReaction by remember(post.id) { mutableStateOf(post.myReaction) }
    val latestPost by rememberUpdatedState(post)
    val latestNow by rememberUpdatedState(now)
    val latestDismiss by rememberUpdatedState(onDismiss)
    val latestReact by rememberUpdatedState(onReact)
    val latestBusy by rememberUpdatedState(busy)
    val haptic = LocalHapticFeedback.current
    val reducedMotion = reduceMotion()
    val colors = SwetgramThemeTokens.colors

    LaunchedEffect(post.id, post.expiresAt) {
        while (latestPost.isActiveAt(latestNow())) {
            currentTime = latestNow()
            delay(250)
        }
        latestDismiss()
    }
    // Celebrate confirmed changes, not a request that could still fail. Opening an already
    // reacted photo must not replay the animation or send another reaction.
    LaunchedEffect(post.id, post.myReaction) {
        if (post.myReaction != null && post.myReaction != previousReaction) {
            burstEmoji = post.myReaction
            burstSequence++
            if (!reducedMotion) haptic.performHapticFeedback(HapticFeedbackType.LongPress)
        }
        previousReaction = post.myReaction
    }
    val transform = rememberTransformableState { _, zoom, pan, _ ->
        scale = (scale * zoom).coerceIn(1f, 5f)
        offset = if (scale == 1f) Offset.Zero else offset + pan
    }
    Dialog(onDismissRequest = onDismiss, properties = swetSheetDialogProperties(dismissOnClickOutside = false)) {
        BoxWithConstraints(Modifier.fillMaxSize().background(colors.backgroundDeep).safeDrawingPadding()) {
            val landscape = maxWidth > maxHeight
            val dockMaxHeight = if (landscape) maxHeight - 80.dp else maxHeight * 0.46f
            val dockWidth = (maxWidth * 0.43f).coerceIn(280.dp, 360.dp)
            Column(Modifier.fillMaxSize().padding(horizontal = 12.dp).padding(top = 4.dp, bottom = 12.dp)) {
                PicsoViewerHeader(
                    post = post, busy = busy, onDismiss = onDismiss, onOpenProfile = onOpenProfile,
                    onDelete = { confirmDelete = true }, onReport = onReport,
                )
                Spacer(Modifier.height(12.dp))

                val photo: @Composable (Modifier) -> Unit = { modifier ->
                    Box(
                        modifier.clip(RoundedCornerShape(24.dp)).background(colors.card)
                            .transformable(transform)
                            .pointerInput(post.id) {
                                detectTapGestures(onDoubleTap = {
                                    if (shouldAddPicsoHeart(latestPost, latestBusy, latestNow())) {
                                        latestReact(PICSO_HEART)
                                    } else if (!latestBusy && latestPost.isActiveAt(latestNow()) && latestPost.myReaction == PICSO_HEART) {
                                        // Instagram-style double tap never toggles an existing heart off.
                                        burstEmoji = PICSO_HEART
                                        burstSequence++
                                    }
                                })
                            },
                    ) {
                        PicsoImage(post, loadImage, now, Modifier.fillMaxSize().graphicsLayer {
                            scaleX = scale; scaleY = scale; translationX = offset.x; translationY = offset.y
                        }, ContentScale.Fit)
                        PicsoLifetimeBadge(
                            remaining = remainingPicsoTime(post.expiresAt, currentTime),
                            modifier = Modifier.align(Alignment.TopStart).padding(12.dp),
                        )
                        burstEmoji?.let { emoji ->
                            PicsoReactionBurst(emoji, burstSequence, Modifier.align(Alignment.Center)) { burstEmoji = null }
                        }
                    }
                }
                val dock: @Composable (Modifier) -> Unit = { modifier ->
                    PicsoViewerActionDock(post, busy, onReact, onToggleSave, onShare, modifier.heightIn(max = dockMaxHeight))
                }
                if (landscape) {
                    Row(Modifier.fillMaxWidth().weight(1f), horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                        photo(Modifier.weight(1f).fillMaxHeight())
                        dock(Modifier.width(dockWidth).align(Alignment.Bottom))
                    }
                } else {
                    photo(Modifier.fillMaxWidth().weight(1f))
                    Spacer(Modifier.height(12.dp))
                    dock(Modifier.fillMaxWidth())
                }
            }
        }
    }
    if (confirmDelete) AlertDialog(
        onDismissRequest = { confirmDelete = false },
        title = { Text(stringResource(Res.string.picso_confirm_delete)) },
        confirmButton = { TextButton(onClick = { confirmDelete = false; onDelete() }, enabled = !busy) { Text(stringResource(Res.string.picso_action_delete)) } },
        dismissButton = { TextButton(onClick = { confirmDelete = false }) { Text(stringResource(Res.string.picso_cancel)) } },
    )
}

internal const val PICSO_HEART = "❤️"
internal val picsoReactionEmojis = listOf(PICSO_HEART, "👍", "😂", "😮", "🔥")

internal fun shouldAddPicsoHeart(post: PicsoPostItem, busy: Boolean, now: Instant): Boolean =
    !busy && post.isActiveAt(now) && post.myReaction != PICSO_HEART

internal fun remainingPicsoTime(expiresAt: String, now: Instant): String {
    val seconds = runCatching { (Instant.parse(expiresAt) - now).inWholeSeconds.coerceAtLeast(0) }.getOrDefault(0)
    return "${seconds / 3600}:${((seconds % 3600) / 60).toString().padStart(2, '0')}:${(seconds % 60).toString().padStart(2, '0')}"
}

@Composable
private fun PicsoReactionBurst(emoji: String, sequence: Int, modifier: Modifier, onFinished: () -> Unit) {
    val progress = remember { Animatable(0f) }
    val reduced = reduceMotion()
    val emojiSize = (72f / LocalDensity.current.fontScale).sp
    val spec = swetTween<Float>(Motion.slow * 3)
    val latestFinished by rememberUpdatedState(onFinished)
    LaunchedEffect(sequence, reduced) {
        progress.snapTo(0f)
        if (reduced) delay(Motion.slow.toLong()) else progress.animateTo(1f, spec)
        latestFinished()
    }
    Box(modifier.size(112.dp).clearAndSetSemantics { }, contentAlignment = Alignment.Center) {
        if (!reduced) Box(Modifier.size(80.dp).graphicsLayer {
            val p = progress.value
            scaleX = 1f + p * 1.6f; scaleY = scaleX
            alpha = 0.13f * (1f - p)
        }.background(Color.White, CircleShape))
        Text(emoji, fontSize = emojiSize, modifier = Modifier.graphicsLayer {
            val p = progress.value
            alpha = if (reduced) 1f else if (p < 0.12f) p / 0.12f else if (p > 0.7f) (1f - p) / 0.3f else 1f
            val pop = if (p < 0.25f) 0.6f + p * 2.6f else 1.25f - (p - 0.25f) / 3f
            scaleX = if (reduced) 1f else pop; scaleY = scaleX
            translationY = if (reduced) 0f else -36.dp.toPx() * p
            rotationZ = if (reduced) 0f else -8f + p * 12f
        })
    }
}
